import { useState, useEffect } from 'react';
import { useSearchParams, Link, Navigate } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useVerifyToken, useAcceptInvite, useDeclineQuote, useQuoteByToken } from '@/hooks/use-accept-invite';
import { useAuthStore } from '@/stores/auth-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import {
  Loader2, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff, XCircle, MessageSquareText,
  FileText, Calendar, Building2, User, PoundSterling, CreditCard,
} from 'lucide-react';

// ─── Decline / Request Changes Dialog ─────────────────────
function DeclineDialog({
  open,
  onOpenChange,
  action,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: 'decline' | 'request_changes';
  onSubmit: (reason?: string) => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState('');

  // Reset reason when dialog closes
  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const handleSubmit = () => {
    onSubmit(reason.trim() || undefined);
    setReason('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            {action === 'decline' ? (
              <><XCircle size={18} className='text-red-400' /> Decline Quote</>
            ) : (
              <><MessageSquareText size={18} className='text-amber-400' /> Request Changes</>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          <p className='text-sm text-[var(--text-secondary)]'>
            {action === 'decline'
              ? 'Are you sure you want to decline this quote? This cannot be undone.'
              : 'Describe the changes you need before accepting this quote.'}
          </p>

          <div className='space-y-1.5'>
            <Label htmlFor='decline-reason'>
              {action === 'decline' ? 'Reason (optional)' : 'What needs to change? *'}
            </Label>
            <Textarea
              id='decline-reason'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                action === 'decline'
                  ? 'e.g., We decided to go with another provider...'
                  : 'e.g., Can you update the pricing for the hosting plan?'
              }
              rows={3}
              required={action === 'request_changes'}
            />
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isPending || (action === 'request_changes' && !reason.trim())}
              variant={action === 'decline' ? 'destructive' : 'default'}
            >
              {isPending && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
              {action === 'decline' ? 'Decline Quote' : 'Request Changes'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Accept Quote Page ────────────────────────────────────
export function AcceptQuotePage() {
  useDocumentTitle('View Quote');

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const { data: tokenData, isLoading: isVerifying, isError: tokenInvalid } = useVerifyToken(token);
  const { data: quoteData, isLoading: isQuoteLoading } = useQuoteByToken(token);
  const acceptInvite = useAcceptInvite();
  const declineQuote = useDeclineQuote();

  // Accept flow state
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Decline / changes dialog state
  const [declineOpen, setDeclineOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);

  // Outcome states
  const [outcome, setOutcome] = useState<'accepted' | 'declined' | 'changes' | null>(null);
  const [depositInvoice, setDepositInvoice] = useState<{ id: string; invoiceNumber: string; total: number; status: string } | null>(null);

  const isTokenMissing = !token;
  const isLoading = isVerifying || isQuoteLoading;
  const inviteData = tokenData?.data;
  const quote = quoteData?.data;

  // ─── Handle Accept ──────────────────────────────────────
  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setAcceptError('');

    if (password.length < 6) {
      setAcceptError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setAcceptError('Passwords do not match.');
      return;
    }

    if (!acceptedTerms) {
      setAcceptError('You must agree to the Terms & Conditions to accept this quote.');
      return;
    }

    try {
      const res = await acceptInvite.mutateAsync({ token, password, acceptedTerms: true });
      if (res.success && res.data) {
        useAuthStore.setState({
          token: res.data.token,
          refreshToken: null,
          user: res.data.user,
          isAuthenticated: true,
        });
        setDepositInvoice(res.data.depositInvoice || null);
        setOutcome('accepted');
        toast({ title: 'Quote accepted!', description: 'Welcome to DynDev Portal.', variant: 'success' });
      }
    } catch (err: any) {
      setAcceptError(err?.message || 'Failed to accept. The link may have expired.');
    }
  };

  // ─── Handle Decline ─────────────────────────────────────
  const handleDecline = async (reason?: string) => {
    if (!quote) return;
    try {
      await declineQuote.mutateAsync({ id: quote.id, token, action: 'decline', reason });
      setOutcome('declined');
      setDeclineOpen(false);
      toast({ title: 'Quote declined', description: 'The team has been notified.' });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message, variant: 'destructive' });
    }
  };

  // ─── Handle Request Changes ─────────────────────────────
  const handleRequestChanges = async (reason?: string) => {
    if (!quote) return;
    try {
      await declineQuote.mutateAsync({ id: quote.id, token, action: 'request_changes', reason });
      setOutcome('changes');
      setChangesOpen(false);
      toast({ title: 'Changes requested', description: 'The team will review your request and get back to you.' });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message, variant: 'destructive' });
    }
  };

  // ─── Token type routing ─────────────────────────────────
  // If token is user_invite (not quote_invite), redirect to set-password
  if (inviteData && inviteData.type === 'user_invite') {
    return <Navigate to={`/set-password?token=${token}`} replace />;
  }

  // ─── Loading state ──────────────────────────────────────
  if (isLoading) {
    return (
      <div className='min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4'>
        <div className='text-center'>
          <Loader2 className='w-8 h-8 animate-spin text-dyndev-green mx-auto mb-3' />
          <p className='text-sm text-[var(--text-secondary)]'>Loading quote...</p>
        </div>
      </div>
    );
  }

  // ─── Invalid / expired token ────────────────────────────
  if (isTokenMissing || tokenInvalid || !inviteData) {
    return (
      <div className='min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4'>
        <div className='w-full max-w-md relative z-10'>
          <div className='text-center mb-8'>
            <h1 className='text-4xl font-bold tracking-tight mb-2'>
              Dyn<span className='text-dyndev-green'>Dev</span>
            </h1>
            <p className='text-[var(--text-secondary)] text-sm'>Project Management Portal</p>
          </div>
          <Card className='shadow-xl'>
            <CardHeader>
              <CardTitle className='text-xl flex items-center gap-2'>
                <AlertCircle size={20} className='text-red-400' />
                {isTokenMissing ? 'Missing Link' : 'Link Expired'}
              </CardTitle>
            </CardHeader>
            <CardContent className='text-center'>
              <p className='text-sm text-[var(--text-secondary)] mb-6'>
                This quote link is invalid or has expired. Please contact your project manager for a new link.
              </p>
              <Link
                to='/login'
                className='inline-flex items-center gap-1 text-sm text-dyndev-green hover:text-dyndev-green/80 transition-colors font-medium'
              >
                <ArrowLeft size={14} />
                Back to Sign In
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── Outcome views ──────────────────────────────────────
  if (outcome) {
    const outcomeConfig = {
      accepted: {
        icon: <CheckCircle size={32} className='text-dyndev-green' />,
        title: 'Quote Accepted!',
        description: 'Your account has been created and the project is now in motion. You are signed in.',
      },
      declined: {
        icon: <XCircle size={32} className='text-red-400' />,
        title: 'Quote Declined',
        description: 'The team has been notified. If this was a mistake, please contact your project manager.',
      },
      changes: {
        icon: <MessageSquareText size={32} className='text-amber-400' />,
        title: 'Changes Requested',
        description: 'The team will review your request and update the quote. You will receive a new link when it is ready.',
      },
    };
    const config = outcomeConfig[outcome];

    return (
      <div className='min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4'>
        <div className='w-full max-w-md relative z-10'>
          <div className='text-center mb-8'>
            <h1 className='text-4xl font-bold tracking-tight mb-2'>
              Dyn<span className='text-dyndev-green'>Dev</span>
            </h1>
          </div>
          <Card className='shadow-xl'>
            <CardContent className='text-center pt-10 pb-8'>
              <div className='flex justify-center mb-6'>
                <div className='w-16 h-16 rounded-full bg-dyndev-green/10 flex items-center justify-center'>
                  {config.icon}
                </div>
              </div>
              <h2 className='text-xl font-semibold mb-2'>{config.title}</h2>
              <p className='text-sm text-[var(--text-secondary)] mb-6'>{config.description}</p>
              {outcome === 'accepted' ? (
                <div className='space-y-3'>
                  {depositInvoice && (
                    <>
                      <div className='p-3 rounded-lg bg-dyndev-green/5 border border-dyndev-green/10 text-sm'>
                        <p className='font-medium text-[var(--text-primary)]'>
                          A deposit invoice has been created
                        </p>
                        <p className='text-xs text-[var(--text-secondary)] mt-1'>
                          {depositInvoice.invoiceNumber} — £{Number(depositInvoice.total || 0).toFixed(2)}
                        </p>
                        <p className='text-xs text-red-400/90 mt-1.5'>
                          Work on your project will begin once your deposit is paid.
                        </p>
                      </div>
                      {['sent', 'overdue'].includes(depositInvoice.status) ? (
                        <Link
                          to={`/invoices?pay=${depositInvoice.id}`}
                          className='inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-dyndev-green text-white text-sm font-medium hover:bg-dyndev-green/90 transition-colors'
                        >
                          <CreditCard size={15} />
                          Pay Deposit Now
                        </Link>
                      ) : (
                        <Link
                          to='/invoices'
                          className='inline-flex items-center gap-2 px-6 py-2.5 rounded-xl border border-dyndev-green/30 text-dyndev-green text-sm font-medium hover:bg-dyndev-green/5 transition-colors'
                        >
                          View Invoice
                        </Link>
                      )}
                    </>
                  )}
                  <div>
                    <Link
                      to='/'
                      className='inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--bg-secondary)] text-[var(--text-primary)] text-sm font-medium border border-[var(--border-color)] hover:bg-[var(--bg-secondary)]/70 transition-colors'
                    >
                      Go to Dashboard
                    </Link>
                  </div>
                </div>
              ) : (
                <Link
                  to='/login'
                  className='inline-flex items-center gap-1 text-sm text-dyndev-green hover:text-dyndev-green/80 transition-colors font-medium'
                >
                  <ArrowLeft size={14} />
                  Back to Sign In
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── Quote not found ────────────────────────────────────
  if (!quote) {
    return (
      <div className='min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4'>
        <div className='w-full max-w-md'>
          <Card className='shadow-xl'>
            <CardHeader>
              <CardTitle className='text-xl flex items-center gap-2'>
                <AlertCircle size={20} className='text-red-400' />
                Quote Not Found
              </CardTitle>
            </CardHeader>
            <CardContent className='text-center'>
              <p className='text-sm text-[var(--text-secondary)]'>
                This quote is no longer available.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── Quote View ─────────────────────────────────────────
  const total = quote.lineItems?.reduce((s: number, li: any) => s + li.total, 0) || 0;
  const lead = quote.lead || {};
  const isQuoteActionable = ['sent', 'revisions_requested'].includes(quote.status);

  // Returning responder: show banner above quote, buttons remain available
  const returningResponder = inviteData && (inviteData.declinedAt || inviteData.changesRequestedAt);

  return (
    <div className='min-h-screen bg-[var(--bg-primary)] py-8 px-4'>
      {/* Noise overlay */}
      <div className='bg-noise opacity-[0.015] pointer-events-none fixed inset-0' />

      <div className='w-full max-w-2xl mx-auto relative z-10'>
        {/* Header */}
        <div className='text-center mb-8'>
          <h1 className='text-4xl font-bold tracking-tight mb-2'>
            Dyn<span className='text-dyndev-green'>Dev</span>
          </h1>
          <p className='text-[var(--text-secondary)] text-sm'>Project Management Portal</p>
        </div>

        {/* Quote Card */}
        <Card className='shadow-xl overflow-hidden'>
          {/* Quote Header */}
          <div className='px-6 py-5 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]'>
            <div className='flex items-start justify-between flex-wrap gap-3'>
              <div>
                <div className='flex items-center gap-2 mb-1'>
                  <FileText size={18} className='text-dyndev-green' />
                  <h2 className='text-lg font-semibold'>{quote.quoteNumber}</h2>
                  <Badge variant='outline' className='text-xs capitalize'>
                    {quote.status.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className='flex flex-wrap items-center gap-4 text-xs text-[var(--text-secondary)] mt-2'>
                  <span className='flex items-center gap-1'>
                    <Calendar size={12} />
                    {new Date(quote.createdAt).toLocaleDateString()}
                  </span>
                  {quote.validUntil && (
                    <span className='flex items-center gap-1'>
                      <AlertCircle size={12} />
                      Valid until {new Date(quote.validUntil).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <div className='text-right'>
                <p className='text-2xl font-bold text-dyndev-green'>
                  £{total.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <CardContent className='p-6'>
            {/* Returning Responder Banner */}
            {returningResponder && (
              <div className='mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-3'>
                <div className='flex-shrink-0 mt-0.5'>
                  {inviteData.declinedAt ? (
                    <XCircle size={16} className='text-red-400' />
                  ) : (
                    <MessageSquareText size={16} className='text-amber-400' />
                  )}
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium text-[var(--text-primary)]'>
                    {inviteData.declinedAt
                      ? `You declined this quote on ${new Date(inviteData.declinedAt).toLocaleDateString()}.`
                      : `You requested changes on ${new Date(inviteData.changesRequestedAt).toLocaleDateString()}.`}
                  </p>
                  {inviteData.lastResponseReason && (
                    <p className='text-xs text-[var(--text-secondary)] mt-0.5'>
                      Reason: &ldquo;{inviteData.lastResponseReason}&rdquo;
                    </p>
                  )}
                  <p className='text-xs text-[var(--text-secondary)] mt-1'>
                    You can still change your decision below.
                  </p>
                </div>
              </div>
            )}

            {/* Company Info */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6'>
              {lead.companyName && (
                <div className='flex items-center gap-2 text-sm'>
                  <Building2 size={14} className='text-[var(--text-secondary)] flex-shrink-0' />
                  <span className='text-[var(--text-secondary)]'>Company:</span>
                  <span className='font-medium'>{lead.companyName}</span>
                </div>
              )}
              {lead.contactName && (
                <div className='flex items-center gap-2 text-sm'>
                  <User size={14} className='text-[var(--text-secondary)] flex-shrink-0' />
                  <span className='text-[var(--text-secondary)]'>Contact:</span>
                  <span className='font-medium'>{lead.contactName}</span>
                </div>
              )}
            </div>

            <Separator className='mb-4' />

            {/* Line Items */}
            <h3 className='text-sm font-semibold mb-3 flex items-center gap-2'>
              <PoundSterling size={14} className='text-dyndev-green' />
              Line Items
            </h3>
            <div className='space-y-2 mb-4'>
              {quote.lineItems?.map((item: any, i: number) => (
                <div
                  key={item.id || i}
                  className='flex items-center justify-between py-2 px-3 rounded-lg bg-[var(--bg-secondary)] text-sm'
                >
                  <div className='flex-1 min-w-0 mr-3'>
                    <p className='truncate'>{item.description}</p>
                    <p className='text-xs text-[var(--text-secondary)]'>
                      {item.quantity} × £{item.unitPrice?.toFixed(2)}
                    </p>
                  </div>
                  <p className='font-medium flex-shrink-0'>£{item.total?.toFixed(2)}</p>
                </div>
              ))}
            </div>

            <Separator className='mb-4' />

            {/* Total */}
            <div className='flex justify-between items-center mb-6'>
              <span className='text-sm font-semibold'>Total</span>
              <span className='text-xl font-bold text-dyndev-green'>£{total.toFixed(2)}</span>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className='mb-6 p-3 rounded-lg bg-[var(--bg-secondary)]'>
                <p className='text-xs text-[var(--text-secondary)] font-medium mb-1'>Notes</p>
                <p className='text-sm'>{quote.notes}</p>
              </div>
            )}

            {/* Action Buttons */}
            {isQuoteActionable && !showAcceptForm && (
              <div className='flex flex-col sm:flex-row gap-2'>
                <Button
                  onClick={() => setShowAcceptForm(true)}
                  size='pill'
                  className='flex-1 bg-dyndev-green hover:bg-dyndev-green/90'
                >
                  Accept Quote
                </Button>
                <Button
                  onClick={() => setChangesOpen(true)}
                  variant='outline'
                  size='pill'
                  className='flex-1'
                >
                  <MessageSquareText size={14} className='mr-1.5' />
                  Request Changes
                </Button>
                <Button
                  onClick={() => setDeclineOpen(true)}
                  variant='ghost'
                  size='pill'
                  className='flex-1 text-red-400 hover:text-red-300 hover:bg-red-500/10'
                >
                  <XCircle size={14} className='mr-1.5' />
                  Decline
                </Button>
              </div>
            )}

            {/* Accept Form (inline) */}
            {showAcceptForm && (
              <form onSubmit={handleAccept} className='space-y-4 border-t border-[var(--border-color)] pt-4'>
                <div className='px-4 py-3 rounded-lg bg-dyndev-green/5 border border-dyndev-green/10'>
                  <p className='text-sm font-medium'>Create your account to accept</p>
                  <p className='text-xs text-[var(--text-secondary)] mt-1'>
                    Set a password to create your DynDev Portal account. You will be automatically signed in.
                  </p>
                </div>

                {acceptError && (
                  <div className='p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm'>
                    {acceptError}
                  </div>
                )}

                <div className='space-y-1.5'>
                  <Label htmlFor='accept-password'>Password *</Label>
                  <div className='relative'>
                    <Input
                      id='accept-password'
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setAcceptError(''); }}
                      placeholder='At least 6 characters'
                      required
                      className='pr-10'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors'
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className='space-y-1.5'>
                  <Label htmlFor='accept-confirm'>Confirm Password *</Label>
                  <div className='relative'>
                    <Input
                      id='accept-confirm'
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setAcceptError(''); }}
                      placeholder='Re-enter your password'
                      required
                      className='pr-10'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors'
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Terms & Conditions acceptance — required (I2) */}
                <label className="flex items-start gap-2.5 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => { setAcceptedTerms(e.target.checked); setAcceptError(''); }}
                    className="mt-0.5 w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-primary)] text-dyndev-green focus:ring-dyndev-green"
                    aria-label="I agree to the Terms & Conditions"
                  />
                  <span className="text-xs text-[var(--text-secondary)] leading-relaxed">
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dyndev-green hover:text-dyndev-green/80 font-medium transition-colors"
                    >
                      Terms &amp; Conditions
                    </Link>{' '}
                    — accepting this quote forms a contract with DynDev Ltd and incorporates those terms.
                  </span>
                </label>

                <div className='flex gap-2'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='pill'
                    onClick={() => { setShowAcceptForm(false); setAcceptError(''); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    disabled={acceptInvite.isPending || !password.trim() || !confirmPassword.trim() || !acceptedTerms}
                    size='pill'
                    className='flex-1 bg-dyndev-green hover:bg-dyndev-green/90'
                  >
                    {acceptInvite.isPending && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
                    {acceptInvite.isPending ? 'Creating Account...' : 'Accept & Create Account'}
                  </Button>
                </div>
              </form>
            )}

            {/* Not actionable state */}
            {!isQuoteActionable && (
              <div className='p-4 rounded-lg bg-amber-500/5 border border-amber-500/10 text-center'>
                <p className='text-sm text-[var(--text-secondary)]'>
                  This quote is no longer available for acceptance.
                  {quote.status === 'accepted' && ' It has already been accepted.'}
                  {quote.status === 'expired' && ' It has expired.'}
                  {quote.status === 'converted' && ' It has been converted to a project.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        {!showAcceptForm && (
          <p className='text-center mt-6 text-xs text-[var(--text-secondary)]'>
            &copy; {new Date().getFullYear()} DynDev. Powered by DynDev Project Management Portal.
          </p>
        )}
      </div>

      {/* Decline Dialog */}
      <DeclineDialog
        open={declineOpen}
        onOpenChange={setDeclineOpen}
        action='decline'
        onSubmit={handleDecline}
        isPending={declineQuote.isPending}
      />

      {/* Request Changes Dialog */}
      <DeclineDialog
        open={changesOpen}
        onOpenChange={setChangesOpen}
        action='request_changes'
        onSubmit={handleRequestChanges}
        isPending={declineQuote.isPending}
      />
    </div>
  );
}
